import { CheckStrokeIcon, Filter } from '@dynamika-shared/ui/icons';
import {
  CustomDropdownProps,
  Dropdown,
} from '@dynamika-shared/ui/Dropdown/Dropdown';
import { useMemo, useState } from 'react';
import { DropdownTag } from './DropdownTag';
import { CustomTooltip } from '@dynamika-shared/ui/Tooltip/CustomTooltip';
import {
  AllFilterItem,
  AllFilters,
  FilterItemType,
  SelectedFilters,
  StateFilterItem,
} from './types';

const defaultSortOption = <Val extends string>(
  a: AllFilterItem<Val>,
  b: AllFilterItem<Val>
) => {
  const labelA = a.label.toUpperCase();
  const labelB = b.label.toUpperCase();
  if (labelA < labelB) return -1;
  if (labelA > labelB) return 1;
  return 0;
};

type NestedFilterProps<
  PF extends string,
  Val extends string,
> = CustomDropdownProps & {
  sortOption?: (a: AllFilterItem<Val>, b: AllFilterItem<Val>) => number;
  subFilters: [PF, StateFilterItem][];
  allFilters: AllFilters<PF, Val>;
  selectedFilters: SelectedFilters<PF>;
  closeTag: (name: PF) => void;
  toggleSelectedFilter: (name: PF, value: string) => void;
};
type FilterItem = {
  key?: string;
  value?: string;
  label: string;
  type?: 'group';
  children?: FilterItem[];
};

const getSelectedFilterLabel = <PF extends string, Val extends string>(
  key: PF,
  allFilters: AllFilters<PF, Val>,
  selectedFilters: SelectedFilters<PF>,
  defaultLabel: string
): string => {
  const selectedValue = selectedFilters?.[key]?.[0];
  if (!selectedValue) return defaultLabel;
  const findLabel = (items: FilterItem[]): string | undefined => {
    for (const item of items) {
      if (item.value === selectedValue || item.key === selectedValue)
        return item.label;
      if (item.type === 'group' && item.children) {
        const childLabel = findLabel(item.children);
        if (childLabel) return childLabel;
      }
    }
    return undefined;
  };

  return findLabel(allFilters[key] as FilterItem[]) ?? defaultLabel;
};
export function NestedFilter<
  PF extends string = string,
  Val extends string = string,
>(props: NestedFilterProps<PF, Val>) {
  const {
    closeTag,
    allFilters,
    selectedFilters,
    toggleSelectedFilter,
    sortOption = defaultSortOption,
    ...otherProps
  } = props;
  const [visible, setVisible] = useState(false);
  const selectedLabels = useMemo(() => {
    return props.subFilters.map(([key, filter]) => {
      return getSelectedFilterLabel(key, allFilters, selectedFilters, filter.label);
    });
  }, [props.subFilters, allFilters, selectedFilters]);

  return (
    <div
      className='filter-container flex items-center gap-[12px]'
      data-testid={'nestedFilter'}
    >
      {props.subFilters.map(([key, filter], index) => {
        return (
          <DropdownTag<PF>
            key={key}
            keyFilter={key}
            onClick={item => toggleSelectedFilter(key, item.key)}
            items={[
              {
                key: '1',
                type: 'group',
                label: filter.label,
                children: [...(allFilters[key] ?? [])]
                  .sort(sortOption)
                  .map((item: FilterItemType) => {
                    const baseItem = {
                      key: item.value,
                      label: (
                        <div
                          className='flex items-center justify-between'
                          data-testid={`${item.label}`}
                        >
                          <CustomTooltip
                            label={item.label}
                            MAX_LABEL_LENGTH={18}
                          />
                          {selectedFilters[key]?.includes(item.value) && (
                            <CheckStrokeIcon width={24} height={24} />
                          )}
                        </div>
                      ),
                    };

                    if (item.type === 'group') {
                      return {
                        ...baseItem,
                        type: 'group' as const,
                        label: item.label,
                        children: item?.children?.map(i => ({
                          ...i,
                          label: (
                            <div
                              className='flex items-center justify-between'
                              data-testid={`${i.label}`}
                            >
                              <CustomTooltip
                                label={i.label}
                                MAX_LABEL_LENGTH={18}
                              />
                              {selectedFilters[key]?.includes(i.key) && (
                                <CheckStrokeIcon width={24} height={24} />
                              )}
                            </div>
                          ),
                        })),
                      };
                    }

                    return baseItem;
                  }),
              },
            ]}
            name={selectedLabels[index]}
            active={filter.active}
            closeTag={closeTag}
          />
        );
      })}
      <div
        role='button'
        className='hover:bg-waterLight flex h-[4rem] cursor-pointer items-center rounded-md'
      >
        <Dropdown
          {...otherProps}
          trigger={['click']}
          open={visible}
          className='h-full p-1'
          onOpenChange={(_, { source }) => {
            if (source === 'trigger') setVisible(!visible);
          }}
        >
          <div className='flex items-center justify-center gap-1'>
            <Filter height={20} width={20} />
            <div className='text-1424'>Filter</div>
          </div>
        </Dropdown>
      </div>
    </div>
  );
}
